import React from 'react';
import { Link } from 'react-router-dom';

const DeveloperResources = () => {
  return (
    <section id="top" className="py-16 bg-gradient-to-br from-blue-600 to-blue-800">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            DEVELOPER RESOURCES
          </h2>
          <p className="text-blue-100 text-lg max-w-3xl mx-auto">
            Comprehensive API documentation and user guides to help you integrate our services seamlessly across Kenya, Tanzania, Uganda, and Ghana
          </p>
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          
          {/* Bulk SMS API Documentation */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <div className="w-full h-64 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-24 h-24 text-white mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                  <h4 className="text-2xl font-bold text-white">SMS API</h4>
                </div>
              </div>
              <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                REST API
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Bulk SMS API Documentation
              </h3>
              <p className="text-blue-600 font-semibold mb-4 text-lg">
                Complete integration guide for SMS services
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Integrate our powerful <strong>Bulk SMS API</strong> into your applications with ease. This comprehensive 
                documentation covers authentication, endpoints, request/response formats, and code examples for 
                both transactional and broadcast SMS services across all supported countries.
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">What's Included:</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Authentication Methods</strong> – API key setup and security best practices</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Complete Endpoints</strong> – Send SMS, check delivery status, manage contacts</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Code Examples</strong> – PHP, Python, Node.js, Java, and cURL samples</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Error Handling</strong> – Complete error codes and troubleshooting guide</span>
                  </div>
                </div>
              </div>

              <p className="text-blue-600 font-semibold mb-6">
                Get started in minutes with our developer-friendly API documentation!
              </p>

              <a
                href="/assets/docs/bulk-sms-api-documentation.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>Open SMS API Documentation</span>
              </a>
            </div>
          </div>

          {/* USSD API Documentation */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <div className="w-full h-64 bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-24 h-24 text-white mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                  </svg>
                  <h4 className="text-2xl font-bold text-white">USSD API</h4>
                </div>
              </div>
              <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                INTERACTIVE
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                USSD Codes API Documentation
              </h3>
              <p className="text-green-600 font-semibold mb-4 text-lg">
                Build interactive USSD applications with ease
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Create powerful <strong>USSD applications</strong> that work on any mobile phone. Our USSD API documentation 
                provides everything you need to build interactive menus, collect user input, and deliver services 
                without requiring internet connectivity or smartphone apps.
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Documentation Features:</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Session Management</strong> – Handle USSD sessions and user flows</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Menu Building</strong> – Create multi-level interactive menus</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Data Collection</strong> – Capture and validate user inputs</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Integration Examples</strong> – Real-world use cases and implementations</span>
                  </div>
                </div>
              </div>

              <p className="text-green-600 font-semibold mb-6">
                Reach every mobile phone with our comprehensive USSD API guide!
              </p>

               <a
                href="/assets/docs/USSD-api-documentation 2.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>Open USSD API Documentation</span>
              </a>
            </div>
          </div>

          {/* Digital Services API Documentation */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
            <div className="relative">
              <div className="w-full h-64 bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-24 h-24 text-white mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <h4 className="text-2xl font-bold text-white">DIGITAL SERVICES API</h4>
                </div>
              </div>
              <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                REAL-TIME
              </div>
            </div>
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                Digital Services API Documentation
              </h3>
              <p className="text-red-600 font-semibold mb-4 text-lg">
                Build immersive digital experiences with our API
              </p>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                Integrate our powerful <strong>Digital Services API</strong> to create engaging digital experiences. 
                This comprehensive documentation covers everything from user authentication to real-time gameplay 
                features, payment processing, and leaderboard management.
              </p>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3">Key Features:</h4>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>User Management</strong> – Player registration, authentication, and profiles</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Real-time Gameplay</strong> – WebSocket integration for live gaming</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Payment Processing</strong> – Secure in-game transactions and purchases</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-green-500 mr-2 text-lg">✔</span>
                    <span className="text-gray-700"><strong>Leaderboards & Achievements</strong> – Track player progress and rankings</span>
                  </div>
                </div>
              </div>

              <p className="text-red-600 font-semibold mb-6">
                Level up your digital platform with our comprehensive API!
              </p>

              <a
                href="/assets/docs/Digital Services.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span>Open Digital Services API Documentation</span>
              </a>
            </div>
          </div>

          {/* Email Service API Documentation */}
<div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
  <div className="relative">
    <div className="w-full h-64 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
      <div className="text-center">
        <svg className="w-24 h-24 text-white mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
        </svg>
        <h4 className="text-2xl font-bold text-white">EMAIL API</h4>
      </div>
    </div>
    <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
      RELIABLE
    </div>
  </div>
  
  <div className="p-8">
    <h3 className="text-2xl font-bold text-gray-800 mb-3">
      Infinity Emailpro API Documentation
    </h3>
    <p className="text-blue-600 font-semibold mb-4 text-lg">
      Send emails and manage subscriptions with our API
    </p>
    
    <p className="text-gray-600 mb-6 leading-relaxed">
      Integrate our simple <strong>Emailpro API</strong> to send messages and manage subscriptions easily. 
      This comprehensive documentation covers everything from sending individual emails to bulk campaigns, 
      subscription management, and convenient MPesa payment processing.
    </p>

    <div className="mb-6">
      <h4 className="font-bold text-gray-800 mb-3">Key Features:</h4>
      <div className="space-y-2">
        <div className="flex items-start">
          <span className="text-green-500 mr-2 text-lg">✔</span>
          <span className="text-gray-700"><strong>Email Sending</strong> – Send individual transactional emails or bulk campaigns</span>
        </div>
        <div className="flex items-start">
          <span className="text-green-500 mr-2 text-lg">✔</span>
          <span className="text-gray-700"><strong>Subscription Management</strong> – Easy plan subscriptions with MPesa payments</span>
        </div>
        <div className="flex items-start">
          <span className="text-green-500 mr-2 text-lg">✔</span>
          <span className="text-gray-700"><strong>Flexible Plans</strong> – Choose from Basic or Standard email packages</span>
        </div>
        <div className="flex items-start">
          <span className="text-green-500 mr-2 text-lg">✔</span>
          <span className="text-gray-700"><strong>Transaction Tracking</strong> – View your email history and subscription status</span>
        </div>
      </div>
    </div>

    <p className="text-blue-600 font-semibold mb-6">
      Simplify your email communication with our easy-to-use API!
    </p>

    <a
      href="/assets/docs/email-service-api-client.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
    >
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <span>Open Email API Documentation</span>
    </a>
  </div>
</div>

          {/* UZA Stock Management User Manual */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 md:col-span-2">
            <div className="relative">
              <div className="w-full h-64 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-24 h-24 text-white mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <h4 className="text-3xl font-bold text-white">UZA - STOCK SYSTEM</h4>
                </div>
              </div>
              <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                USER MANUAL
              </div>
            </div>
            
            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    UZA Stock Management System User Manual
                  </h3>
                  <p className="text-purple-600 font-semibold mb-4 text-lg">
                    Complete guide to managing your inventory efficiently
                  </p>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Master the <strong>UZA Stock Management System</strong> with our comprehensive user manual. 
                    From basic navigation to advanced reporting features, this guide covers everything you need 
                    to streamline your inventory management and boost your business efficiency.
                  </p>

                  <p className="text-purple-600 font-semibold mb-6">
                    Transform your inventory management with UZA's powerful features!
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Manual Sections:</h4>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>Getting Started</strong> – System setup and initial configuration</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>Product Management</strong> – Add, edit, and organize your inventory</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>Sales & Purchases</strong> – Process transactions and manage suppliers</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>Reports & Analytics</strong> – Generate insights and track performance</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>User Management</strong> – Configure roles and permissions</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2 text-lg">✔</span>
                      <span className="text-gray-700"><strong>Troubleshooting</strong> – Common issues and solutions</span>
                    </div>
                  </div>

                  <a
                  href="/assets/docs/uza-stock-management-user-manual.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <span>View UZA User Manual</span>
                </a>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto border border-white/20">
            <h3 className="text-2xl font-bold text-white mb-4">
              Need Additional Support?
            </h3>
            <p className="text-blue-100 mb-6 text-lg">
              Our technical team is here to help you integrate and implement our solutions successfully
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Technical Support
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeveloperResources;